import { API_ERROR_MESSAGES } from 'constants/api-messages'
import { REGEX } from 'constants/common'
import { ERROR_MESSAGES } from 'constants/messages'
import { getClientList } from 'services/client.service'
import { getDepartmentList } from 'services/department.service'
import { getManagerList } from 'services/manager.service'
import * as yup from 'yup'
import { validateBaselineQuarterValue, validateTargetQuarterValue, yupEmptyStringOr } from './yupHelpers'

let timerName: NodeJS.Timeout
let timerEmail: NodeJS.Timeout

export const createClientSchema = yup.object().shape({
  name: yup
    .string()
    .required(ERROR_MESSAGES.F_TEXT_REQUIRED)
    .max(256, ERROR_MESSAGES.F_TEXT_MAXLENGTH_256)
    .matches(REGEX.EXCLUDE_SPECIAL_CHAR, ERROR_MESSAGES.F_TEXT_NO_SPEC_CHAR)
    .when(['clientName'], (clientName, schema) =>
      schema.test({
        test: (value) => {
          return new Promise((resolve) => {
            if (clientName[0] && clientName[0].toLowerCase() === value.trim().toLowerCase()) {
              return resolve(true)
            }
            clearTimeout(timerName)
            timerName = setTimeout(async () => {
              const response = await getClientList()
              const items = response.items || []
              const nameExists: boolean = items.some(
                (item: any) => item.name.toLowerCase() === value.trim().toLowerCase()
              )
              resolve(!nameExists)
            }, 1000)
          })
        },
        message: ERROR_MESSAGES.F_TEXT_UNREGISTER_CLIENT_NAME
      })
    ),

  representer: yup
    .string()
    .required(ERROR_MESSAGES.F_TEXT_REQUIRED)
    .max(256, ERROR_MESSAGES.F_TEXT_MAXLENGTH_256)
    .matches(REGEX.EXCLUDE_SPECIAL_CHAR, ERROR_MESSAGES.F_TEXT_NO_SPEC_CHAR),
  email: yup
    .string()
    .required(ERROR_MESSAGES.F_TEXT_REQUIRED)
    .matches(REGEX.EMAIL, ERROR_MESSAGES.F_TEXT_EMAIL_FORMAT)
    .max(256, ERROR_MESSAGES.F_TEXT_MAXLENGTH_256)
    .when(['clientEmail'], (clientEmail, schema) =>
      schema.test({
        test: (value) => {
          return new Promise((resolve) => {
            if (clientEmail[0] && clientEmail[0].toLowerCase() === value.trim().toLowerCase()) {
              return resolve(true)
            }
            clearTimeout(timerEmail)
            timerEmail = setTimeout(async () => {
              const response = await getManagerList({ email: value.trim().toLowerCase() })
              const items = response.items || []
              const emailExists: boolean = items.length !== 0
              resolve(!emailExists)
            }, 1000)
          })
        },
        message: API_ERROR_MESSAGES.EMAIL_ALREADY_TAKEN
      })
    ),
  startFiscalYear: yup.number().required(ERROR_MESSAGES.F_SELECT_REQUIRED)
})

export const createManagerSchema = yup.object().shape({
  name: yup
    .string()
    .required(ERROR_MESSAGES.F_TEXT_REQUIRED)
    .max(256, ERROR_MESSAGES.F_TEXT_MAXLENGTH_256)
    .matches(REGEX.EXCLUDE_SPECIAL_CHAR, ERROR_MESSAGES.F_TEXT_NO_SPEC_CHAR),
  email: yup
    .string()
    .required(ERROR_MESSAGES.F_TEXT_REQUIRED)
    .matches(REGEX.EMAIL, ERROR_MESSAGES.F_TEXT_EMAIL_FORMAT)
    .max(256, ERROR_MESSAGES.F_TEXT_MAXLENGTH_256)
    .when(['clientId', 'clientEmail'], (client, schema) =>
      schema.test({
        test: (value) => {
          return new Promise((resolve) => {
            if (client[1] && client[1].toLowerCase() === value.trim().toLowerCase()) {
              return resolve(true)
            }
            clearTimeout(timerEmail)
            timerEmail = setTimeout(async () => {
              const response = await getManagerList({ email: value.trim().toLowerCase() })
              const items = response.items || []
              const emailExists: boolean = items.length !== 0
              resolve(!emailExists)
            }, 1000)
          })
        },
        message: API_ERROR_MESSAGES.EMAIL_ALREADY_TAKEN
      })
    ),
  role: yup.string().required(ERROR_MESSAGES.F_SELECT_REQUIRED)
})

export const createDepartmentSchema = yup.object().shape({
  name: yup
    .string()
    .required(ERROR_MESSAGES.F_TEXT_REQUIRED)
    .max(256, ERROR_MESSAGES.F_TEXT_MAXLENGTH_256)
    .matches(REGEX.EXCLUDE_SPECIAL_CHAR, ERROR_MESSAGES.F_TEXT_NO_SPEC_CHAR)
    .when(['clientId', 'departmentName'], (client, schema) =>
      schema.test({
        test: (value) => {
          return new Promise((resolve) => {
            if (client[1] && client[1].toLowerCase() === value.trim().toLowerCase()) {
              return resolve(true)
            }
            clearTimeout(timerName)
            timerName = setTimeout(async () => {
              const response = await getDepartmentList({ clientId: client[0] })
              const items = response.items || []
              const nameExists: boolean = items.some(
                (item: any) => item.name.toLowerCase() === value.trim().toLowerCase()
              )
              resolve(!nameExists)
            }, 1000)
          })
        },
        message: ERROR_MESSAGES.F_TEXT_UNREGISTER_DEPT_NAME
      })
    ),
  adminId: yup.number()
})

export const createKPISettingSchema = yup.object().shape({
  metric: yup.string().required(ERROR_MESSAGES.F_TEXT_REQUIRED),
  entity: yup.string().required(ERROR_MESSAGES.F_SELECT_REQUIRED),
  category: yup.string().required(ERROR_MESSAGES.F_SELECT_REQUIRED),
  metricType: yup.string().required(ERROR_MESSAGES.F_SELECT_REQUIRED),
  targetType: yup.string().required(ERROR_MESSAGES.F_SELECT_REQUIRED),
  unit: yup.string().required(ERROR_MESSAGES.F_SELECT_REQUIRED),
  baseline: yupEmptyStringOr(yup.number()),
  baselineQ1: validateBaselineQuarterValue('baselineQ1'),
  baselineQ2: validateBaselineQuarterValue('baselineQ2'),
  baselineQ3: validateBaselineQuarterValue('baselineQ3'),
  baselineQ4: validateBaselineQuarterValue('baselineQ4'),
  target: yupEmptyStringOr(yup.number()),
  targetQ1: validateTargetQuarterValue('targetQ1'),
  targetQ2: validateTargetQuarterValue('targetQ2'),
  targetQ3: validateTargetQuarterValue('targetQ3'),
  targetQ4: validateTargetQuarterValue('targetQ4')
})

export const updateDepartmentKPISchema = (quarter: number, isRoleDepHead: boolean) =>
  isRoleDepHead
    ? yup.object()
    : yup.object().shape({
        staffKpis: yup.array().of(
          yup.object().shape({
            [`weightageQ${quarter}`]: yup.number().when([`statusQ${quarter}`], (value, schema) => {
              return value[0] ? schema.required('') : schema.notRequired()
            })
          })
        )
      })

export const updateStaffWeightage = (quarter: number) => {
  return yup.object().shape({
    staffs: yup.array().of(
      yup.object().shape({
        [`weightageQ${quarter}`]: yup.number().required('')
      })
    )
    // .test('sum-of-percentage', 'The sum of the percentages must be 100', (contacts) => {

    //   const sum =
    //     contacts?.reduce((acc, contact) => {
    //       return acc + contact.percentage
    //     }, 0) || 0
    //   return sum === 100
    // })
  })
}
